import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { StateController } from '../../../helpers/state/state-management/state-controller';
import tinycolor from '@ctrl/tinycolor';
import topicData from '../../../helpers/state/topic-data';

/**
 * Calculates the WCAG contrast ratio between two colors.
 * Returns a value between 1 (no contrast) and 21 (max contrast).
 * WCAG AA requires 4.5:1 for normal text, 3:1 for large text.
 */
function getContrastRatio(color1: string, color2: string): number {
	const lum1 = tinycolor(color1).getLuminance();
	const lum2 = tinycolor(color2).getLuminance();
	const lighter = Math.max(lum1, lum2);
	const darker = Math.min(lum1, lum2);
	return (lighter + 0.05) / (darker + 0.05);
}

@customElement('header-colors')
export class HeaderColors extends LitElement {

	@property({ type: Boolean })
    open: boolean = false;

	@state()
	private contrastRatio: number = 21;

	private readonly bindTopicState = new StateController(this, topicData);

	private updateContrastWarning() {
		const bg = topicData.topic?.header_color;
		const text = topicData.topic?.header_text_color;
		if (bg && text) {
			this.contrastRatio = getContrastRatio(bg, text);
		} else {
			this.contrastRatio = 21; // assume good if not set
		}
	}

	updated() {
		this.updateContrastWarning();
	}

	async onChangeHeaderColor(e: Event) {
		const headerColor = (e.target as HTMLInputElement).value;

		await topicData.fetchAndUpdateTopicData({
			header_color: headerColor
		}, 'header_color');
		this.updateContrastWarning();
	}

	async onChangeHeaderTextColor(e: Event) {
		const headerTextColor = (e.target as HTMLInputElement).value;

		await topicData.fetchAndUpdateTopicData({
			header_text_color: headerTextColor
		}, 'header_text_color');
		this.updateContrastWarning();
	}

	private get contrastWarningLevel(): 'pass' | 'large-only' | 'fail' {
		if (this.contrastRatio >= 4.5) return 'pass';
		if (this.contrastRatio >= 3.0) return 'large-only';
		return 'fail';
	}

    render() {
        return html`
			<h4 class="header-colors-label">
                Colors
            </h4>
			<div class="color__wrapper">
				<color-picker
					id="headerColor"
					.open="${this.open}"
					@change=${this.onChangeHeaderColor}
					class="input"
					label="Banner Color"
					value="${topicData.topic?.header_color ?? ''}">
				</color-picker>

				<color-picker
					id="headerTextColor"
					@change=${this.onChangeHeaderTextColor}
					.open="${this.open}"
					class="input"
					label="Text Color"
					value="${topicData.topic?.header_text_color ?? ''}">
				</color-picker>
			</div>
			${this.contrastWarningLevel === 'fail' ? html`
				<div class="contrast-warning contrast-warning--fail">
					<span class="contrast-warning__icon">&#9888;</span>
					<span>Low contrast (${this.contrastRatio.toFixed(1)}:1). WCAG requires at least 4.5:1 for text readability. Users with visual impairments may not be able to read your content.</span>
				</div>
			` : this.contrastWarningLevel === 'large-only' ? html`
				<div class="contrast-warning contrast-warning--warn">
					<span class="contrast-warning__icon">&#9888;</span>
					<span>Contrast ratio is ${this.contrastRatio.toFixed(1)}:1 — meets WCAG for large text only. For full accessibility, aim for at least 4.5:1.</span>
				</div>
			` : html`
				<span> In some cases, Proxi may override the text color to ensure readability. </span>
			`}
		`;
    }

    static get styles() {
        return css`
		    .input {
                width: 100%;
                padding: auto 5px;
            }

			.color__wrapper {
				display: flex;
				flex-direction: row;
				align-items: center;
				gap: 5px;
			}

			.header-colors-label {
                margin: 10px 0;
            }

			.contrast-warning {
				display: flex;
				align-items: flex-start;
				gap: 6px;
				padding: 8px 10px;
				border-radius: 4px;
				font-size: 0.75rem;
				line-height: 1.4;
				margin-top: 6px;
			}

			.contrast-warning--fail {
				background-color: #fef2f2;
				border: 1px solid #fca5a5;
				color: #991b1b;
			}

			.contrast-warning--warn {
				background-color: #fffbeb;
				border: 1px solid #fcd34d;
				color: #92400e;
			}

			.contrast-warning__icon {
				flex-shrink: 0;
				font-size: 1rem;
			}
        `;
    }
}
